import React, { ReactElement } from 'react';
import { Animated, StyleSheet, Text, View } from 'react-native';

import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import { isWeb } from '@helpers';
import { scale } from '@components/frame';

import TouchableElement from '@components/common/touchableElement';
import LayeredModal from '@components/modal/LayeredModal';

import { useManageDropdown } from './hooks/useManageDropdown';
import {
  HOVER_TRANSITION_MS,
  MENU_INITIAL_SCALE,
  MENU_INITIAL_TRANSLATE_Y,
} from './constants';
import type { DropdownProps } from './types';
import { getBoxShadowStyle } from '@styles/shadow';

function Dropdown<T extends string = string>({
  C,
  items,
  selectedValue,
  onChange,
  placeholder,
  testID,
  testIDForOption,
  renderTrigger,
  menuStyle,
  optionStyle,
  optionTextStyle,
  menuAlignment = 'right',
  selectedIcon,
  withOptionDivider,
}: DropdownProps<T>) {
  const {
    triggerRef,
    open,
    shouldRenderDropdownMenu,
    position,
    selectedLabel,
    animProgress,
    handleOpen,
    handleClose,
    handleSelect,
  } = useManageDropdown({ items, selectedValue, onChange });
  const boxShadowStyle = getBoxShadowStyle({C, type: 'Field'});

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ triggerRef, onPress: handleOpen, open, selectedLabel })
      ) : (
        <TouchableElement testID={testID} onPress={handleOpen} style={{ width: '100%' }}>
          {({ boxStyle }) => (
            <View
              ref={triggerRef}
              style={{
                borderWidth: 1,
                borderColor: C('Hairline/Light/No trans [mobile]'),
                borderRadius: scale(8),
                paddingHorizontal: scale(12),
                paddingVertical: scale(10),
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: scale(4),
                ...boxStyle,
              }}
            >
              <Text style={[font['Subhead 2'], { color: C('Label/Light/Primary') }]}>
                {selectedLabel || placeholder || ''}
              </Text>
              <Svg icon="chevronBottom" color={C('Label/Light/Tertiary')} width={scale(12)} height={scale(10)} />
            </View>
          )}
        </TouchableElement>
      )}
      {shouldRenderDropdownMenu && (
        <LayeredModal
          C={C}
          isVisible={open}
          onBackdropPress={handleClose}
          backdropColor="transparent"
          centered={false}
          animationInTiming={1}
          animationOutTiming={1}
        >
          <Animated.View
            style={[
              boxShadowStyle,
              {
                position: 'absolute',
                top: position.top,
                ...(menuAlignment === 'left' ? { left: position.left } : { right: position.right }),
                width: position.width,
                backgroundColor: C('Background/Light/Modal Highlight'),
                borderRadius: scale(8),
                overflow: 'hidden',
                opacity: animProgress,
                transform: [
                  {
                    translateY: animProgress.interpolate({
                      inputRange: [0, 1],
                      outputRange: [scale(MENU_INITIAL_TRANSLATE_Y), 0],
                    }),
                  },
                  {
                    scale: animProgress.interpolate({
                      inputRange: [0, 1],
                      outputRange: [MENU_INITIAL_SCALE, 1],
                    }),
                  },
                ],
              },
              menuStyle,
            ]}
          >
            {items.map((item, index) => {
              const isSelected = item.value === selectedValue;
              const isLast = index === items.length - 1;
              return (
                <TouchableElement key={item.value} testID={testIDForOption?.(item.value)} onPress={handleSelect(item.value)}>
                  {({ hovered, boxStyle }) => {
                    const styleParams = { isSelected, hovered };
                    const resolvedOptionStyle = typeof optionStyle === 'function' ? optionStyle(styleParams) : optionStyle;
                    const resolvedOptionTextStyle =
                      typeof optionTextStyle === 'function' ? optionTextStyle(styleParams) : optionTextStyle;
                    return (
                      <View
                        style={[
                          boxStyle,
                          {
                            paddingHorizontal: scale(12),
                            paddingVertical: scale(10),
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: scale(8),
                            backgroundColor: hovered
                              ? C('Selected/Light/Secondary')
                              : C('Background/Light/Modal Highlight'),
                          },
                          withOptionDivider && !isLast && {
                            borderBottomColor: C('Hairline/Light/No trans'),
                            borderBottomWidth: StyleSheet.hairlineWidth,
                          },
                          isWeb && {
                            transitionProperty: 'background-color',
                            transitionDuration: `${HOVER_TRANSITION_MS}ms`,
                          },
                          resolvedOptionStyle,
                        ]}
                      >
                        <Text
                          style={[
                            font['Subhead 2'],
                            {
                              color: isSelected
                                ? C('Other/Light/Logo/Smart')
                                : C('Label/Light/Primary'),
                            },
                            resolvedOptionTextStyle,
                          ]}
                        >
                          {item.label}
                        </Text>
                        {isSelected && selectedIcon}
                      </View>
                    );
                  }}
                </TouchableElement>
              );
            })}
          </Animated.View>
        </LayeredModal>
      )}
    </>
  );
}

export default ColorWrapper(Dropdown as (props: DropdownProps) => ReactElement) as <T extends string = string>(
  props: Omit<DropdownProps<T>, 'C'>,
) => ReactElement;
